import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import api from "../services/api";

// Async Thunks for Party CRUD operations

// Get all parties
export const getAllParties = createAsyncThunk(
  "party/getAllParties",
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await api.get("/parties");
      return data.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || error.message || "Failed to fetch parties"
      );
    }
  }
);

// Get my parties (created by logged-in user)
export const getMyParties = createAsyncThunk(
  "party/getMyParties",
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await api.get("/parties/my");
      return data.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || error.message || "Failed to fetch your parties"
      );
    }
  }
);

// Get single party by ID
export const getPartyById = createAsyncThunk(
  "party/getPartyById",
  async (id, { rejectWithValue }) => {
    try {
      const { data } = await api.get(`/party/${id}`);
      return data.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || error.message || "Failed to fetch party"
      );
    }
  }
);

// Create new party
export const createParty = createAsyncThunk(
  "party/createParty",
  async (partyData, { rejectWithValue }) => {
    try {
      const { data } = await api.post("/party", partyData);
      return data.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 
        error.response?.data?.errors?.join(", ") || 
        error.message || 
        "Failed to create party"
      );
    }
  }
);

// Update party
export const updateParty = createAsyncThunk(
  "party/updateParty",
  async ({ id, partyData }, { rejectWithValue }) => {
    try {
      const { data } = await api.put(`/party/${id}`, partyData);
      return data.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 
        error.response?.data?.errors?.join(", ") || 
        error.message || 
        "Failed to update party"
      );
    }
  }
);

// Delete party
export const deleteParty = createAsyncThunk(
  "party/deleteParty",
  async (id, { rejectWithValue }) => {
    try {
      await api.delete(`/party/${id}`);
      return id;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || error.message || "Failed to delete party"
      );
    }
  }
);

// Search parties
export const searchParties = createAsyncThunk(
  "party/searchParties",
  async (searchQuery, { rejectWithValue }) => {
    try {
      const { data } = await api.get(`/parties/search?search=${searchQuery}`);
      return data.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || error.message || "Failed to search parties"
      );
    }
  }
);

// Get parties with pagination
export const getPartiesWithPagination = createAsyncThunk(
  "party/getPartiesWithPagination",
  async ({ page = 1, limit = 10 }, { rejectWithValue }) => {
    try {
      const { data } = await api.get(`/parties/paginated?page=${page}&limit=${limit}`);
      return data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || error.message || "Failed to fetch parties"
      );
    }
  }
);

// Party Slice
const partySlice = createSlice({
  name: "party",
  initialState: {
    parties: [],
    currentParty: null,
    loading: false,
    error: null,
    success: false,
    message: null,
    pagination: {
      page: 1,
      limit: 10,
      total: 0,
      totalPages: 0,
    },
  },
  reducers: {
    clearErrors: (state) => {
      state.error = null;
    },
    clearMessage: (state) => {
      state.message = null;
    },
    resetSuccess: (state) => {
      state.success = false;
    },
    setCurrentParty: (state, action) => {
      state.currentParty = action.payload;
    },
    clearCurrentParty: (state) => {
      state.currentParty = null;
    },
  },
  extraReducers: (builder) => {
    // Get all parties
    builder.addCase(getAllParties.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(getAllParties.fulfilled, (state, action) => {
      state.loading = false;
      state.parties = action.payload;
      state.error = null;
    });
    builder.addCase(getAllParties.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
    });

    // Get my parties
    builder.addCase(getMyParties.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(getMyParties.fulfilled, (state, action) => {
      state.loading = false;
      state.parties = action.payload;
      state.error = null;
    });
    builder.addCase(getMyParties.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
    });

    // Get party by ID
    builder.addCase(getPartyById.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(getPartyById.fulfilled, (state, action) => {
      state.loading = false;
      state.currentParty = action.payload;
      state.error = null;
    });
    builder.addCase(getPartyById.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
    });

    // Create party
    builder.addCase(createParty.pending, (state) => {
      state.loading = true;
      state.error = null;
      state.success = false;
    });
    builder.addCase(createParty.fulfilled, (state, action) => {
      state.loading = false;
      state.success = true;
      state.parties.unshift(action.payload);
      state.message = "Party created successfully";
      state.error = null;
    });
    builder.addCase(createParty.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
      state.success = false;
    });

    // Update party
    builder.addCase(updateParty.pending, (state) => {
      state.loading = true;
      state.error = null;
      state.success = false;
    });
    builder.addCase(updateParty.fulfilled, (state, action) => {
      state.loading = false;
      state.success = true;
      state.message = "Party updated successfully";
      state.parties = state.parties.map((party) =>
        party._id === action.payload._id ? action.payload : party
      );
      if (state.currentParty?._id === action.payload._id) {
        state.currentParty = action.payload;
      }
      state.error = null;
    });
    builder.addCase(updateParty.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
      state.success = false;
    });

    // Delete party
    builder.addCase(deleteParty.pending, (state) => {
      state.loading = true;
      state.error = null;
      state.success = false;
    });
    builder.addCase(deleteParty.fulfilled, (state, action) => {
      state.loading = false;
      state.success = true;
      state.message = "Party deleted successfully";
      state.parties = state.parties.filter(
        (party) => party._id !== action.payload
      );
      if (state.currentParty?._id === action.payload) {
        state.currentParty = null;
      }
      state.error = null;
    });
    builder.addCase(deleteParty.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
      state.success = false;
    });

    // Search parties
    builder.addCase(searchParties.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(searchParties.fulfilled, (state, action) => {
      state.loading = false;
      state.parties = action.payload;
      state.error = null;
    });
    builder.addCase(searchParties.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
    });

    // Get parties with pagination
    builder.addCase(getPartiesWithPagination.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(getPartiesWithPagination.fulfilled, (state, action) => {
      state.loading = false;
      state.parties = action.payload.data;
      state.pagination = {
        page: action.payload.page,
        limit: action.payload.limit || 10,
        total: action.payload.total,
        totalPages: action.payload.totalPages,
      };
      state.error = null;
    });
    builder.addCase(getPartiesWithPagination.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
    });
  },
});

export const {
  clearErrors,
  clearMessage,
  resetSuccess,
  setCurrentParty,
  clearCurrentParty,
} = partySlice.actions;

export default partySlice.reducer;

